"use client";

import { SOCIAL_LINKS } from "@guru/shared/constants/social-links";
import { useTranslation } from "@guru/shared/i18n";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useTheme } from "@/components/theme-provider";

interface FooterLink {
  label: string;
  href: string;
  external?: boolean;
}

export function PublicFooter() {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);

  const { resolvedTheme } = useTheme();

  useEffect(() => {
    setMounted(true);
  }, []);

  const columns: Array<{ heading: string; links: FooterLink[] }> = [
    {
      heading: "Product",
      links: [
        { label: t.nav.features, href: "/home#features" },
        { label: "Fee calculator", href: "/pricing#calculator" },
        { label: t.nav.roadmap, href: "/roadmap" },
        { label: t.nav.pricing, href: "/pricing" },
      ],
    },
    {
      heading: t.footer.company,
      links: [
        { label: t.footer.blog, href: "/blog" },
        { label: t.footer.contact, href: "/contact" },
        { label: t.footer.aboutEtheraEdge, href: "https://etheraedge.com", external: true },
      ],
    },
    {
      heading: t.footer.legal,
      links: [
        { label: t.footer.terms, href: "/terms" },
        { label: t.footer.privacy, href: "/privacy" },
        { label: "Data processing", href: "/dpa" },
        { label: "Acceptable use", href: "/acceptable-use" },
        { label: "Refund policy", href: "/refund-policy" },
        { label: "Cookie preferences", href: "/cookies" },
        { label: "Grievance Officer", href: "/grievance-officer" },
        { label: "Legal", href: "/legal" },
      ],
    },
  ];

  return (
    <footer className="relative border-t border-border bg-foreground text-background overflow-hidden">
      <div className="grain-overlay" />
      <div className="relative z-10 mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-[1.4fr_repeat(3,1fr)]">
          {/* Brand */}
          <div>
            <Image
              src={mounted && resolvedTheme === "dark" ? "/logo.png" : "/logo-dark.png"}
              alt="EtheraGuru"
              width={120}
              height={40}
              className="h-8 w-auto"
            />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-background/70">
              {t.footer.tagline}
            </p>
            <div className="mt-4 space-y-1 text-sm text-background/70">
              <a
                href="mailto:corporate@etheraedge.com"
                className="block hover:text-background transition-colors"
              >
                corporate@etheraedge.com
              </a>
              <a href="tel:+918138037385" className="block hover:text-background transition-colors">
                +91 8138037385
              </a>
            </div>
            <div className="mt-5 flex items-center gap-4">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-background/50 hover:text-background transition-colors"
                  aria-label={social.name}
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {columns.map((column) => (
            <nav key={column.heading} aria-label={column.heading}>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-background/50">
                {column.heading}
              </h2>
              <ul className="mt-4 space-y-2.5">
                {column.links.map((link) => (
                  <li key={link.href}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-background/70 hover:text-background transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        href={link.href}
                        className="text-sm text-background/70 hover:text-background transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        {/* Bottom */}
        <div className="mt-10 flex flex-col items-center justify-between gap-2 border-t border-background/20 pt-6 sm:flex-row">
          <p className="text-xs text-background/50">
            &copy; {new Date().getFullYear()} {t.footer.copyright}
          </p>
          <p className="text-xs text-background/50">{t.footer.builtFor}</p>
        </div>
      </div>
    </footer>
  );
}
